import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-garment-try-on/artifacts/skm3z4j3_Photoroom_20251125_232246.jpeg";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password
      }, { withCredentials: true });

      login(response.data);
      toast.success("Welcome to TailorGlam!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0A0A0A]" data-testid="signup-page">
      {/* Left - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1517502166878-35c93a0072f0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Fashion"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-4xl mb-4">Join TailorGlam</h2>
          <p className="text-white/70 font-light">
            Get 3 free virtual try-ons to experience the future of fashion.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img src={LOGO_URL} alt="TailorGlam" className="w-10 h-10 object-contain" />
            <span className="font-heading text-2xl font-medium tracking-tight text-white">
              TailorGlam
            </span>
          </Link>

          <h1 className="font-heading text-3xl mb-2 text-white">Create Account</h1>
          <p className="text-gray-400 mb-8">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF1493] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>

          {/* Google Signup */}
          <button
            onClick={handleGoogleSignup}
            className="w-full h-12 border border-white/20 rounded-none flex items-center justify-center gap-3 hover:bg-white/5 mb-6 text-white"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-medium">Continue with Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-[#0A0A0A] text-sm text-gray-500">or</span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-300">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="mt-2 h-12 rounded-none bg-[#141414] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF1493] focus:ring-[#FF1493]"
                required
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="mt-2 h-12 rounded-none bg-[#141414] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF1493] focus:ring-[#FF1493]"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-300">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (min 6 characters)"
                  className="h-12 rounded-none bg-[#141414] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF1493] focus:ring-[#FF1493] pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 spinner"></div>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="mt-6 text-sm text-gray-500 text-center">
            By signing up, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
